/**
 * useMapLayers — All Mapbox layer setup and update callbacks for MapboxMap.
 * Manages: icon loading, destination/POI/waterway layer creation and updates.
 */
import { useState, useCallback } from 'react';
import { DESTINATION_ICON } from '@/utils/map-icons';
import { updateWaterwaysLayer as updateWaterwaysLayerFn } from './waterwayLayers';

export default function useMapLayers() {
  const [iconsLoaded, setIconsLoaded] = useState(false);

  // Load all map icons (destination + POI icons)
  const loadIcons = useCallback((map) => {
    return new Promise((resolve) => {
      const waitForStyle = () => {
        if (!map.isStyleLoaded()) {
          map.once('style.load', () => {
            addAllIcons();
          });
        } else {
          addAllIcons();
        }
      };

      const addAllIcons = () => {
        const iconPromises = [];

        // Load destination icon (base64 inline)
        if (!map.hasImage('destination-icon')) {
          iconPromises.push(new Promise((res) => {
            const img = new Image();
            img.onload = () => {
              try {
                if (map.isStyleLoaded() && !map.hasImage('destination-icon')) {
                  map.addImage('destination-icon', img);
                }
              } catch (e) {
              }
              res();
            };
            img.onerror = () => {  res(); };
            img.src = DESTINATION_ICON;
          }));
        }

        // Load all 15 POI icons from /icons/poi/
        const poiIconFiles = {
          'poi-fishing-access': '/icons/poi/fishing-access@2x.png',
          'poi-boat-ramp': '/icons/poi/boat-ramp@2x.png',
          'poi-stream-gauge': '/icons/poi/stream-gauge@2x.png',
          'poi-rapids': '/icons/poi/rapids@2x.png',
          'poi-dam': '/icons/poi/dam@2x.png',
          'poi-waterfall': '/icons/poi/waterfall@2x.png',
          'poi-bridge': '/icons/poi/bridge@2x.png',
          'poi-hazard': '/icons/poi/hazard@2x.png',
          'poi-fly-shop': '/icons/poi/fly-shop@2x.png',
          'poi-lodging': '/icons/poi/lodging@2x.png',
          'poi-campsite': '/icons/poi/campsite@2x.png',
          'poi-trailhead': '/icons/poi/trailhead@2x.png',
          'poi-parking': '/icons/poi/parking@2x.png',
          'poi-restroom': '/icons/poi/restroom@2x.png',
          'poi-other': '/icons/poi/other@2x.png',
          'poi-waypoint': '/icons/poi/waypoint@2x.png'
        };

        Object.entries(poiIconFiles).forEach(([iconId, iconPath]) => {
          if (!map.hasImage(iconId)) {
            iconPromises.push(new Promise((res) => {
              map.loadImage(iconPath, (error, image) => {
                if (error) {
                  res();
                  return;
                }
                try {
                  if (!map.hasImage(iconId)) {
                    map.addImage(iconId, image, { pixelRatio: 2 });
                  }
                } catch (e) {
                }
                res();
              });
            }));
          }
        });

        Promise.all(iconPromises).then(() => {
          setIconsLoaded(true);
          resolve();
        });
      };

      waitForStyle();
    });
  }, []);

  // Add or update destination layers imperatively
  const updateLayers = useCallback((map, destData, forceRecreate = false) => {
    if (!map || !map.isStyleLoaded()) return;

    try {
      const hasDestIcon = map.hasImage('destination-icon');

      const destLayer = map.getLayer('destinations-layer');
      const shouldRecreateDestLayer = forceRecreate ||
        (destLayer && hasDestIcon && destLayer.type === 'circle');

      if (shouldRecreateDestLayer) {
        ['destinations-label', 'destinations-layer'].forEach(layerId => {
          if (map.getLayer(layerId)) map.removeLayer(layerId);
        });
        ['destinations'].forEach(sourceId => {
          if (map.getSource(sourceId)) map.removeSource(sourceId);
        });
        // Also clean up legacy access-points layers if present
        ['access-points-label', 'access-points-layer'].forEach(layerId => {
          if (map.getLayer(layerId)) map.removeLayer(layerId);
        });
        if (map.getSource('access-points')) map.removeSource('access-points');
      }

      // Update or add destinations source
      const destSource = map.getSource('destinations');
      if (destSource) {
        destSource.setData(destData);
      } else {
        map.addSource('destinations', { type: 'geojson', data: destData });
      }

      // Add destinations layer
      if (!map.getLayer('destinations-layer')) {
        if (hasDestIcon) {
          map.addLayer({
            id: 'destinations-layer',
            type: 'symbol',
            source: 'destinations',
            layout: {
              'icon-image': 'destination-icon',
              'icon-size': [
                'interpolate', ['linear'], ['zoom'],
                3, 0.03, 5, 0.05, 7, 0.08, 9, 0.1, 11, 0.12, 13, 0.15, 15, 0.18, 18, 0.22
              ],
              'icon-allow-overlap': true,
              'icon-ignore-placement': true
            }
          });
        } else {
          map.addLayer({
            id: 'destinations-layer',
            type: 'circle',
            source: 'destinations',
            paint: {
              'circle-radius': ['interpolate', ['linear'], ['zoom'], 3, 4, 6, 5, 10, 6, 14, 7, 18, 8],
              'circle-color': '#f59e0b',
              'circle-stroke-width': 2,
              'circle-stroke-color': '#ffffff'
            }
          });
        }
      }

      // Add label layer
      if (!map.getLayer('destinations-label')) {
        map.addLayer({
          id: 'destinations-label',
          type: 'symbol',
          source: 'destinations',
          minzoom: 10,
          layout: {
            'text-field': ['get', 'name'],
            'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
            'text-size': ['interpolate', ['linear'], ['zoom'], 10, 10, 14, 12, 18, 14],
            'text-offset': [0, 1.5],
            'text-anchor': 'top',
            'text-max-width': 8,
            'text-optional': true
          },
          paint: {
            'text-color': '#1e40af',
            'text-halo-color': '#ffffff',
            'text-halo-width': 1.5
          }
        });
      }
    } catch (error) {
    }
  }, []);

  // Update POI layers — dual-layer: circles (zoomed out) + symbols with labels (zoomed in)
  const updatePOILayer = useCallback((map, poiData, showPOI, forceRecreate = false) => {
    if (!map) return;

    try {
      // Force-recreate if legacy single-layer exists
      if (!forceRecreate) {
        const existingLayer = map.getLayer('pois-layer');
        if (existingLayer && existingLayer.type === 'circle') {
          forceRecreate = true;
        }
      }

      if (forceRecreate) {
        ['pois-label', 'pois-layer', 'pois-circles'].forEach(layerId => {
          if (map.getLayer(layerId)) map.removeLayer(layerId);
        });
        if (map.getSource('pois')) map.removeSource('pois');
      }

      // Update or add source
      const poiSource = map.getSource('pois');
      if (poiSource) {
        poiSource.setData(poiData);
      } else {
        map.addSource('pois', { type: 'geojson', data: poiData });
      }

      const beforeLayer = map.getLayer('destinations-layer') ? 'destinations-layer' : undefined;

      // Layer 1: Circle layer (zoomed out, z6-z11)
      if (!map.getLayer('pois-circles')) {
        map.addLayer({
          id: 'pois-circles',
          type: 'circle',
          source: 'pois',
          minzoom: 6,
          maxzoom: 11,
          paint: {
            'circle-radius': ['interpolate', ['linear'], ['zoom'], 6, 1.5, 9, 3, 11, 4.5],
            'circle-color': ['get', 'color'],
            'circle-opacity': ['interpolate', ['linear'], ['zoom'], 10, 1, 11, 0],
            'circle-stroke-width': 0.5,
            'circle-stroke-color': 'rgba(255,255,255,0.6)',
            'circle-stroke-opacity': ['interpolate', ['linear'], ['zoom'], 10, 1, 11, 0],
          },
          layout: {
            'visibility': showPOI ? 'visible' : 'none'
          }
        }, beforeLayer);
      } else {
        map.setLayoutProperty('pois-circles', 'visibility', showPOI ? 'visible' : 'none');
      }

      // Layer 2: Symbol layer with icons + labels (zoomed in, fades in z10-z11)
      if (!map.getLayer('pois-layer')) {
        map.addLayer({
          id: 'pois-layer',
          type: 'symbol',
          source: 'pois',
          minzoom: 6,
          layout: {
            'icon-image': ['get', 'icon'],
            'icon-size': [
              'interpolate', ['linear'], ['zoom'],
              6, 0, 10, 0, 11, 0.2, 14, 0.3
            ],
            'icon-allow-overlap': true,
            'icon-ignore-placement': false,
            'icon-padding': 2,
            'text-field': ['step', ['zoom'], '', 12, ['get', 'name']],
            'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
            'text-size': ['interpolate', ['linear'], ['zoom'], 12, 9, 14, 11],
            'text-offset': [0, 1.4],
            'text-anchor': 'top',
            'text-max-width': 8,
            'text-optional': true,
            'text-allow-overlap': false,
            'visibility': showPOI ? 'visible' : 'none'
          },
          paint: {
            'text-color': '#FFFFFF',
            'text-halo-color': 'rgba(0,0,0,0.7)',
            'text-halo-width': 1,
          }
        }, beforeLayer);
      } else {
        map.setLayoutProperty('pois-layer', 'visibility', showPOI ? 'visible' : 'none');
      }

      // Remove legacy separate label layer if it exists
      if (map.getLayer('pois-label')) {
        map.removeLayer('pois-label');
      }

    } catch (error) {
    }
  }, []);

  // Update waterways layer - delegated to extracted module
  const updateWaterwaysLayer = useCallback((map, showRivers, showLakes, forceRecreate = false) => {
    updateWaterwaysLayerFn(map, showRivers, showLakes, forceRecreate);
  }, []);

  return {
    iconsLoaded,
    setIconsLoaded,
    loadIcons,
    updateLayers,
    updatePOILayer,
    updateWaterwaysLayer,
  };
}
